import { isContactWorkflow } from "./workflowParticipants";
import { enqueueSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";

export default function EmailPreviewDialog({
  open,
  onClose,
  api,
  template,
  ensureSaved,
  senders = [],
  workflowId,
  workflow,
}) {
  const contactWorkflow = isContactWorkflow(workflow);
  const participantLabel = contactWorkflow ? "outreach contact" : "user";
  const [search, setSearch] = useState("");
  const [users, setUsers] = useState([]);
  const [user, setUser] = useState(null);
  const [recipient, setRecipient] = useState("");
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [useRealUser, setUseRealUser] = useState(false);
  const [sender, setSender] = useState(null);
  useEffect(() => {
    if (!open || !useRealUser) return;
    let active = true;
    const timer = setTimeout(async () => {
      setUsersLoading(true);
      try {
        const searchMethod = contactWorkflow
          ? "searchEligibleContacts"
          : "searchEligibleUsers";
        const result = await api[searchMethod]({
          workflowId,
          search,
          first: 10,
        });
        if (active) setUsers((result.edges || []).map((e) => e.node));
      } catch (requestError) {
        if (active) setError(requestError.message);
      } finally {
        if (active) setUsersLoading(false);
      }
    }, 300);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [api, open, search, useRealUser, workflowId, contactWorkflow]);
  useEffect(() => {
    if (open) {
      setPreview(null);
      setError("");
      setRecipient("");
      setUser(null);
      setUsers([]);
      setSearch("");
      setUseRealUser(false);
      setSender(senders[0] || null);
    }
  }, [open, template, senders, workflowId, contactWorkflow]);
  const resolve = async () => {
    setError("");
    setBusy(true);
    try {
      const saved = await ensureSaved(template);
      if (!saved?.id)
        throw new Error("Save the draft before previewing this email.");
      return saved;
    } catch (e) {
      enqueueSnackbar(e.message, { variant: "error" });
      return null;
    } finally {
      setBusy(false);
    }
  };
  const showPreview = async () => {
    if (useRealUser && !user)
      return enqueueSnackbar(
        `Choose an eligible ${participantLabel} for merge-field context.`,
        { variant: "error" },
      );
    const saved = await resolve();
    if (!saved) return;
    setBusy(true);
    try {
      setPreview(
        await api.previewTemplate(
          saved.id,
          contactWorkflow ? null : user?.id || null,
          !useRealUser,
          sender?.id || null,
          contactWorkflow ? user?.id || null : null,
        ),
      );
    } catch (requestError) {
      enqueueSnackbar(requestError.message, { variant: "error" });
    } finally {
      setBusy(false);
    }
  };
  const send = async () => {
    if (useRealUser && !user)
      return enqueueSnackbar(
        `Choose an eligible ${participantLabel} for merge-field context.`,
        { variant: "error" },
      );
    if (!/^\S+@\S+\.\S+$/.test(recipient))
      return enqueueSnackbar("Enter a valid internal test email address.", {
        variant: "error",
      });
    const saved = await resolve();
    if (!saved) return;
    setBusy(true);
    try {
      const result = await api.testTemplate(
        saved.id,
        contactWorkflow ? null : user?.id || null,
        recipient,
        !useRealUser,
        sender?.id || null,
        contactWorkflow ? user?.id || null : null,
      );
      if (!result.success)
        throw new Error(result.message || "Unable to send test email.");
      enqueueSnackbar(result.message || "Test email sent.", {
        variant: "success",
      });
    } catch (requestError) {
      enqueueSnackbar(requestError.message, { variant: "error" });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Preview and test email</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {error && <Alert severity="error">{error}</Alert>}
          <Alert severity="info">
            Preview uses example details for Alex Taylor at Oakwood School.
          </Alert>
          {senders.length === 0 && (
            <Alert severity="warning">
              Assign an active sender to this workflow before previewing or
              sending this email.
            </Alert>
          )}
          <Autocomplete
            options={senders}
            value={sender}
            getOptionLabel={(option) =>
              `${option.displayName} <${option.fromEmail}>`
            }
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(_, value) => {
              setSender(value);
              setPreview(null);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Preview as sender"
                helperText="The recipient keeps one sender throughout their journey."
              />
            )}
          />
          <FormControlLabel
            label={`Test with a real ${participantLabel}`}
            control={
              <Switch
                checked={useRealUser}
                onChange={(event) => {
                  setUseRealUser(event.target.checked);
                  setUser(null);
                  setPreview(null);
                }}
              />
            }
          />
          {useRealUser && (
            <Autocomplete
              options={users}
              value={user}
              inputValue={search}
              loading={usersLoading}
              filterOptions={(options) => options}
              getOptionLabel={(option) =>
                `${option.firstName || ""} ${option.lastName || ""}`.trim()
                  ? `${option.firstName || ""} ${option.lastName || ""} (${option.email})`.trim()
                  : option.email
              }
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_, value) => {
                setUser(value);
                setPreview(null);
              }}
              onInputChange={(_, value, reason) => {
                if (reason !== "reset") setSearch(value);
              }}
              slotProps={{ listbox: { sx: { maxHeight: 280 } } }}
              noOptionsText={
                search
                  ? `No eligible ${participantLabel}s found`
                  : `No eligible ${participantLabel}s`
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={`Merge-field context ${participantLabel}`}
                  helperText="Type a name or email address to search"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {usersLoading && <CircularProgress size={18} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          )}
          <Button onClick={showPreview} disabled={busy || !sender}>
            Generate preview
          </Button>
          {preview && (
            <Box sx={{ p: 2, bgcolor: "background.default", borderRadius: 1 }}>
              <Typography variant="subtitle2">{preview.subject}</Typography>
              {preview.bodyHtml ? (
                <Box
                  component="iframe"
                  title="Rendered email preview"
                  sandbox=""
                  srcDoc={preview.bodyHtml}
                  sx={{
                    width: "100%",
                    minHeight: 260,
                    border: 0,
                    bgcolor: "white",
                  }}
                />
              ) : (
                <Typography
                  component="pre"
                  sx={{ whiteSpace: "pre-wrap", fontFamily: "inherit" }}
                >
                  {preview.bodyText}
                </Typography>
              )}
              {preview.ctaUrl && (
                <Link href={preview.ctaUrl} target="_blank" rel="noreferrer">
                  Final CTA: {preview.ctaUrl}
                </Link>
              )}
            </Box>
          )}
          <TextField
            label="Internal test recipient"
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
          />
          <Button variant="contained" onClick={send} disabled={busy || !sender}>
            Send test email
          </Button>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
